import React, { useState, useRef } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import PayrollEdit from "./PayrollEdit";
import { useTranslation } from "react-i18next";
import { Months } from "../../../../utils";
import Print from "./Print";

const PayrollList = () => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [filters, setFilters] = useState({ month: "", year: "", employee: "" });
  const { handleDelete, ConfirmDialog } = useDelete("employee-payroll", token);
  const { data: employees } = useFetchObjects(["employees"], "users/", token);
  const endpoint = `employee-payroll/?month=${filters.month}&year=${filters.year}&employee=${filters.employee}`;
  const { data, isLoading, isError, error } = useFetchObjects(
    ["employee-payroll", filters],
    endpoint,
    token
  );

  const [open, setOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const handleOpen = (item) => {
    setSelectedPayroll(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPayroll(null);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handlePrintClick = (item) => {
    setPrintData(item);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  // Format date and time separately for two-line display
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "", time: "" };
    try {
      const locale = i18n.language || "en-US";

      const dateOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      const timeOptions = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };

      const datePart = new Intl.DateTimeFormat(locale, dateOptions).format(
        new Date(dateString)
      );
      const timePart = new Intl.DateTimeFormat(locale, timeOptions).format(
        new Date(dateString)
      );
      return { date: datePart, time: timePart };
    } catch {
      return { date: dateString, time: "" };
    }
  };

  if (isLoading) {
    return <Typography>{t("hr.loading")}</Typography>;
  }

  if (isError) {
    return <Typography color="error">{error.message}</Typography>;
  }

  return (
    <Grid container padding={2}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            label={t("hr.month")}
            name="month"
            value={filters.month}
            onChange={handleFilterChange}
            fullWidth
            size="small"
          >
            {Months.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            label={t("hr.year")}
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            fullWidth
            size="small"
          >
            {Array.from(
              { length: new Date().getFullYear() - 2020 + 1 },
              (_, index) => {
                const year = 2020 + index;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              }
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            label={t("hr.employee")}
            name="employee"
            value={filters.employee}
            onChange={handleFilterChange}
            fullWidth
            size="small"
          >
            {(employees ?? []).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {`${item.first_name} ${item.last_name}`}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <TableContainer
        sx={{
          maxWidth: "100%",
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: 1,
        }}
      >
        <Table
          aria-label="payroll table"
          sx={{ minWidth: 650, borderCollapse: "separate", borderSpacing: 0 }}
        >
          <TableHead
            sx={{
              backgroundColor: "#f5f5dc", // beige / weach color
            }}
          >
            <TableRow>
              {[
                t("hr.employee"),
                t("hr.month"),
                t("hr.year"),
                t("hr.totalSalary"),
                t("hr.advanceAmount"),
                t("hr.bonusAmount"),
                t("hr.paidSalary"),
                t("hr.retentionAmount"),
                t("hr.remainingSalary"),
                t("hr.date"), // renamed from createdAt
                t("hr.update"),
                t("hr.delete"),
                t("hr.print"),
              ].map((header, idx) => (
                <TableCell
                  key={idx}
                  align="center"
                  sx={{
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    px: 1.5,
                    py: 1.2,
                    borderBottom: "1px solid #ccc",
                    borderRight: idx !== 12 ? "1px solid #ccc" : "none",
                  }}
                  title={header}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((item, index) => {
              const { date, time } = formatDateTime(item.created_at);
              return (
                <TableRow
                  key={item.id || index}
                  hover
                  sx={{
                    cursor: "default",
                    "&:hover": {
                      backgroundColor: "rgba(0, 123, 255, 0.1)",
                    },
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: 150,
                      borderRight: "1px solid #eee",
                    }}
                    title={item.employee_display}
                  >
                    {item.employee_display}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.month_display}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.year}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.total_salary}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.total_advance}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.total_bonus}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.paid_salary}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.retention_amount}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    {item.remaining_salary}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      whiteSpace: "nowrap",
                      borderRight: "1px solid #eee",
                      lineHeight: 1.2,
                    }}
                    title={`${date} ${time}`}
                  >
                    {date}
                    <br />
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                    >
                      {time}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        boxShadow: "none",
                        textTransform: "capitalize",
                        color: "green",
                        backgroundColor: "#d0f0c0",
                        "&:hover": {
                          backgroundColor: "#4caf50",
                          color: "white",
                        },
                      }}
                      onClick={() => handleOpen(item)}
                    >
                      {t("hr.update")}
                    </Button>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ whiteSpace: "nowrap", borderRight: "1px solid #eee" }}
                  >
                    <Button
                      onClick={() => handleDelete(item.id)}
                      size="small"
                      variant="contained"
                      sx={{
                        boxShadow: "none",
                        textTransform: "capitalize",
                        color: "white",
                        backgroundColor: "#f44336",
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                        },
                      }}
                    >
                      {t("hr.delete")}
                    </Button>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      color="primary"
                      onClick={() => handlePrintClick(item)}
                    >
                      <PrintIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog />

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("hr.editPayroll")}</DialogTitle>
        <DialogContent>
          <PayrollEdit
            payroll={selectedPayroll}
            onClose={handleClose}
            token={token}
          />
        </DialogContent>
      </Dialog>

      {/* Hidden Print Component */}
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <Print data={printData} />
        </div>
      )}
    </Grid>
  );
};

export default PayrollList;
